import { faPlusSquare, faUpload, faDownload, faSpinner, faCircleXmark, faPencil } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, useEffect, useRef } from 'react'
import { ImageConfig } from './ImageConfig'
import { groupClaimEvidence, parseExtraContent, getExtraContent, extraFieldKey, evidenceFields, evidenceDates, validateEvidence, evidenceApiError } from './claimEvidence'

import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import useAuth from '../../../../hooks/useAuth'
import moment from 'moment'
import { actionEndDateLimits, documentDeadline } from '../claimActionRules'

const ViewClaim = ({ claim, policy }) => {
  const { auth, createInfo } = useAuth();

  const axios = useAxiosPrivate()
  
  const [phasedReturn, setPhasedReturn] = useState(claim?.claim_phased || false);
  const [showPolicyDetails, setShowPolicyDetails] = useState(false)
  const [uploadErrors, setUploadErrors] = useState({})
  const [uploadingSections, setUploadingSections] = useState({})
  const [evidenceSections, setEvidenceSections] = useState([])
  const [evidenceLoading, setEvidenceLoading] = useState(true)
  const [evidenceError, setEvidenceError] = useState('')
  const [evidenceVersion, setEvidenceVersion] = useState(0)
  const [commAttachments, setCommAttachments] = useState([])
  const [savingPhased, setSavingPhased] = useState(false)
  const [documentDrafts, setDocumentDrafts] = useState({})
  const [savingDocuments, setSavingDocuments] = useState({})
  const [documentErrors, setDocumentErrors] = useState({})
  const [selectedFile, setSelectedFile] = useState()
  const [isSuccess, setIsSuccess] = useState(false)
  const [errorMsg, setErrorMsg] = useState(false)
  const [showFiles, setShowFiles] = useState([])
  const [fileList, setFileList] = useState([])
  const [endDate, setEndDate] = useState('')
  const [membershipNumber, setMembershipNumber] = useState(claim?.naht_member || '')
  const [savingAction, setSavingAction] = useState('')
  const savedEndDate = claim?.claim_absence_end_date || claim?.end_date
  const endDateLimits = actionEndDateLimits(claim, policy)
  const expiryDate = documentDeadline(claim)

  useEffect(() => {
    setEndDate('')
    setMembershipNumber(claim?.naht_member || '')
  }, [claim?.claim_id, claim?.naht_member])

  const saveClaimAction = async (action, fields) => {
    if (savingAction) return
    setSavingAction(action)
    try {
      const { data } = await axios.post(`/v1/staff-hub/claims/${action}/${claim.claim_id}?api_token=${process.env.REACT_APP_KIS_TOKEN}`, {
        updated_by: auth?.user?.id ?? auth?.user?.contact_id,
        ...fields,
      })
      if (data?.error === true) throw new Error(data.message || 'Unable to update the claim.')
      window.location.reload()
    } catch (error) {
      createInfo('error', error.response?.data?.message || error.message || 'Unable to update the claim.')
    } finally {
      setSavingAction('')
    }
  }

  const saveEndDate = () => {
    if (!moment(endDate, 'YYYY-MM-DD', true).isValid() ||
      (endDateLimits.min && endDate < endDateLimits.min) ||
      (endDateLimits.max && endDate > endDateLimits.max)) {
      createInfo('error', 'Select a last date of absence within the allowed dates.')
      return
    }
    saveClaimAction('end-date', { claim_absence_end_date: endDate })
  }

  const wrapperRef = useRef(null)

  const allowedFileTypes = [
    '.doc',
    '.docx',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.xls',
    '.xlsx',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    '.csv',
    'application/pdf',
    '.pdf',
    'image/jpeg',
    '.jpg',
    'image/png',
    '.png',
    'image/tiff',
    '.tiff',
  ];

  useEffect(() => {
    let active = true;
    const fetchEvidence = async () => {
      if (!claim?.claim_id) {
        setEvidenceLoading(false);
        return;
      }
      setEvidenceLoading(true);
      setEvidenceError('');
      setEvidenceSections([]);

      try {
      const { data } = await axios.get(`/v1/staff-hub/claims/${claim.claim_id}/evidence?api_token=${process.env.REACT_APP_KIS_TOKEN}`);
      const transformed = groupClaimEvidence(data).map(item => ({
        ...item,
        visible: false,
        uploadValues: {},
      }))
      if (active) setEvidenceSections(transformed);
      } catch {
        if (active) setEvidenceError('Unable to load claim documents. Please reload the page to try again.');
      } finally {
        if (active) setEvidenceLoading(false);
      }
    };

    fetchEvidence();
    return () => { active = false; };
  }, [claim?.claim_id, evidenceVersion]);

  useEffect(() => {
    setPhasedReturn(Boolean(Number(claim?.claim_phased ?? claim?.claim_info?.phased ?? 0)))
  }, [claim?.claim_id, claim?.claim_phased, claim?.claim_info?.phased])

  const handleTogglePhasedReturn = async () => {
    if (savingPhased || phasedReturn) return
    setPhasedReturn(true)
    setSavingPhased(true)

    try {
      const { data } = await axios.post(`/v1/staff-hub/claims/phased/${claim.claim_id}?api_token=${process.env.REACT_APP_KIS_TOKEN}`, {
        updated_by: auth.user.id ?? auth.user.contact_id,
        claim_phased: true,
      })
      if (data?.error === true) throw new Error(data.message)
      setEvidenceVersion(version => version + 1)
    } catch {
      setPhasedReturn(false)
      createInfo('error', 'Failed to update Phased Return.')
    } finally {
      setSavingPhased(false)
    }
  }

  const toggleSectionVisibility = (id) => {
    setFileList([])
    setEvidenceSections((prev) =>
      prev.map((section) =>
        ({ ...section, visible: section.evidence_id === id ? !section.visible : false })
      )
    )
  }

  const updateSectionDate = (id, field, value) => {
    setEvidenceSections((prev) =>
      prev.map((section) =>
        section.evidence_id === id ? { ...section, [field]: value } : section
      )
    )
  }

  const handleUpload = async (section) => {
    if (uploadingSections[section.evidence_id]) return
    const file = fileList[0]
    const values = section.uploadValues || {}
    const validationError = validateEvidence(section, values, { file, upload: true })
    setUploadErrors(prev => ({ ...prev, [section.evidence_id]: validationError }))
    if (validationError) return
    setUploadingSections(prev => ({ ...prev, [section.evidence_id]: true }))
    const dates = evidenceDates(section, values)
    const extraContent = evidenceFields(section).filter(field => !field.isDate).map(({ key, isDate, ...field }) => ({
      ...field, content: values[key] || '',
    }))
    const formData = new FormData()
    formData.append('file', file)
    formData.append('claim_id', claim.claim_id)
    formData.append('evidence_id', section.evidence_id)
    // Multipart cannot encode null; dates are sent as nullable JSON in claims/evidence.
    Object.entries(dates).forEach(([key, value]) => { if (value !== null) formData.append(key, value) })

    formData.append("extra_content", JSON.stringify(extraContent))

    try {
      const uploadRes = await axios.post(
        `/v1/staff-hub/claims/upload/document?area=policy/claims/${claim.claim_id}&type=claim&cid=${claim.claim_id}&api_token=${process.env.REACT_APP_KIS_TOKEN}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      if (uploadRes.data?.error) throw new Error(evidenceApiError(uploadRes.data, 'Upload failed'))
      if (uploadRes?.data?.data?.resource_id) {
        const evidencePayload = {
          claim_id: claim.claim_id,
          evidence_id: section.evidence_id,
          ...dates,
          extra_content: JSON.stringify(extraContent),
          resource_id: uploadRes.data.data.resource_id,
        };

        const evidenceResponse = await axios.post(`/v1/staff-hub/claims/evidence?api_token=${process.env.REACT_APP_KIS_TOKEN}`, evidencePayload);
        if (evidenceResponse.data?.error === true) throw new Error(evidenceApiError(evidenceResponse.data, 'Unable to save evidence.'));

        const startMatch = claim.claim_absence_start_date === dates.valid_start;
        const endMatch = claim.claim_absence_end_date === dates.valid_until;

        if (
          (section.evidence_id === '00499e10-33cc-11e7-80f2-cbd5f9bacaa9' && startMatch) ||
          (section.evidence_id === '00499e10-33cc-11e7-80f2-cbd5f9bacaa8' && startMatch && endMatch)
        ) {
          window.location.reload()
        } else {
          createInfo('success', 'Document uploaded successfully')
          setFileList([])
          setEvidenceVersion(version => version + 1)
        }
      } else {
        throw new Error('File uploaded but no resource ID returned.')
      }
    } catch (error) {
      setUploadErrors(prev => ({ ...prev, [section.evidence_id]: evidenceApiError(error.response?.data, error.message || 'Upload failed') }))
    }
    setUploadingSections(prev => ({ ...prev, [section.evidence_id]: false }))
  }

  const onDragEnter = () => wrapperRef.current.classList.add('dragover')
  const onDragLeave = () => wrapperRef.current.classList.remove('dragover')

  const onDragOver = (e) => {
    e.stopPropagation()
    e.preventDefault()
  }
  const onDrop = (e) => {
    e.preventDefault()
    wrapperRef.current.classList.remove('dragover')
    onFileDrop(e.nativeEvent.dataTransfer.files)
  }

  const isValidFileType = (file) => {
    const extension = `.${file.name.split('.').pop()}`
    const mimeType = file.type
    return (
      allowedFileTypes.includes(extension) ||
      allowedFileTypes.includes(mimeType)
    )
  }
  const onFileUpload = async (e) => {
    e.preventDefault()
    const newFile = e.target.files[0]

    if (newFile && isValidFileType(newFile)) {
      const updatedList = [...fileList, newFile]
      setFileList(updatedList)
    } else {
      createInfo('error', 'Invalid file type. Please select a valid file.')
    }
  }

  const onFileDrop = async (files) => {
    const newFile = files[0]

    if (newFile && isValidFileType(newFile)) {
      const updatedList = [...fileList, newFile]
      setFileList(updatedList)
    } else {
      createInfo('error', 'Invalid file type')
      return
    }
  }

  const fileRemove = (file) => {
    const updatedList = [...fileList]
    updatedList.splice(fileList.indexOf(file), 1)
    setFileList(updatedList)
  }

  const cancelDocumentEdit = (id) => {
    setDocumentDrafts(prev => ({ ...prev, [id]: null }))
    setDocumentErrors(prev => ({ ...prev, [id]: '' }))
  }

  const updateDocument = (id, changes) => {
    setEvidenceSections(prev => prev.map(section => ({
      ...section,
      documents: section.documents.map(doc => doc.evidence_document_id === id ? { ...doc, ...changes } : doc),
    })))
  }

  const saveDocument = async (doc, section) => {
    const id = doc.evidence_document_id
    if (savingDocuments[id]) return
    const draft = documentDrafts[id]
    const fields = evidenceFields(section, doc)
    const validationError = validateEvidence(section, draft, { doc })
    if (validationError) {
      setDocumentErrors(prev => ({ ...prev, [id]: validationError }))
      return
    }
    setSavingDocuments(prev => ({ ...prev, [id]: true }))
    setDocumentErrors(prev => ({ ...prev, [id]: '' }))
    try {
      // The API updates dates individually. Keep confirmed changes if a later request fails.
      const dates = evidenceDates(section, draft)
      const dateKeys = dates.valid_start && doc.valid_until && dates.valid_start > moment(doc.valid_until).format('YYYY-MM-DD')
        ? ['valid_until', 'valid_start'] : ['valid_start', 'valid_until']
      for (const key of doc.evidence_document_status === 'accepted' ? [] : dateKeys) {
        const field = { key }
        const original = doc[field.key] ? moment(doc[field.key]).format('YYYY-MM-DD') : ''
        if ((dates[field.key] || '') === original) continue
        const { data } = await axios.post(`/v1/staff-hub/claims/evidence_document/${id}?api_token=${process.env.REACT_APP_KIS_TOKEN}`, {
          column: field.key,
          [field.key]: dates[field.key],
        })
        if (data?.error) throw new Error(evidenceApiError(data, 'Unable to save document dates.'))
        updateDocument(id, { [field.key]: dates[field.key] })
      }
      const extras = parseExtraContent(doc.extra_content)
      const changedFields = fields.filter(field => !field.isDate && draft[field.key] !== getExtraContent(extras, field.key))
      if (changedFields.length) {
        const updated = extras.map(field => ({ ...field }))
        changedFields.forEach(field => {
          const existing = updated.find(extra => extraFieldKey(extra.label) === extraFieldKey(field.key))
          if (existing) existing.content = draft[field.key]
          else updated.push({ label: field.label, content: draft[field.key] })
        })
        const extra_content = JSON.stringify(updated)
        const { data } = await axios.post(`/v1/staff-hub/claims/evidence_document/${id}/extra_content?api_token=${process.env.REACT_APP_KIS_TOKEN}`, { extra_content })
        if (data?.error) throw new Error(evidenceApiError(data, 'Unable to save document details.'))
        updateDocument(id, { extra_content })
      }
      cancelDocumentEdit(id)
      createInfo('success', 'Document updated')
    } catch (error) {
      setDocumentErrors(prev => ({ ...prev, [id]: `${evidenceApiError(error.response?.data, error.message || 'Unable to save document.')} Some changes may have saved. Please retry the remaining changes.` }))
    } finally {
      setSavingDocuments(prev => ({ ...prev, [id]: false }))
    }
  }

  return (
    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <label className="block text-base font-semibold text-sas mb-3">Last date of absence</label>
          {savedEndDate ? (
            <p className="rounded-md bg-green-50 p-4 text-sm font-medium text-green-700">
              The last date of absence is {moment(savedEndDate).format('DD MMM YYYY')}.
            </p>
          ) : (
            <>
          <p className="mb-3 text-sm text-gray-600">Please provide details of the last day of absence for this claim.</p>
          <input
            type="date"
            aria-label="Last date of absence"
            value={endDate}
            onChange={event => setEndDate(event.target.value)}
            min={endDateLimits.min}
            max={endDateLimits.max}
            placeholder="Enter last date"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            disabled={Boolean(savingAction)}
          />
          {endDate && <button type="button" onClick={saveEndDate} disabled={Boolean(savingAction)} className="mt-3 rounded-md bg-sas px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{savingAction === 'end-date' ? 'Saving...' : 'Save'}</button>}
            </>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <label className="block text-base font-semibold text-sas mb-3">Phased return</label>
          {!phasedReturn && (<p className="mb-3 text-sm text-gray-600">Let us know if this person is returning on a phased return to work.</p>)}
          <div
            className={`relative inline-flex w-11 shrink-0 rounded-full p-0.5 transition-colors duration-200 ease-in-out 
                          ${phasedReturn ? 'bg-green-600' : 'bg-gray-200'}
                          ${savingPhased || phasedReturn ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}
                        `}
          >
            <input
              id="phased-return-toggle"
              name="phased-return"
              type="checkbox"
              disabled={savingPhased || phasedReturn}
              className="peer absolute inset-0 z-10 opacity-0 cursor-pointer disabled:cursor-not-allowed"
              checked={phasedReturn}
              onChange={handleTogglePhasedReturn}
              aria-label="Phased return"
              aria-busy={savingPhased}
            />
            <span className="relative size-5 rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out peer-checked:translate-x-5">
              {phasedReturn ? (
                <span className="absolute inset-0 flex items-center justify-center">
                  <svg fill="currentColor" viewBox="0 0 12 12" className="size-3 text-green-600">
                    <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                  </svg>
                </span>
              ) : (
                <span className="absolute inset-0 flex items-center justify-center">
                  <svg fill="none" viewBox="0 0 12 12" className="size-3 text-gray-400">
                    <path
                      d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
            </span>
          </div>
          {savingPhased && <p role="status" className="mt-2 flex items-center gap-2 text-sm text-sas"><FontAwesomeIcon icon={faSpinner} spin />Saving phased return...</p>}
        </div>
      </div>

      {policy?.department === 'NAHT' && (
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-base font-semibold text-sas">Are you a NAHT member?</h2>
          <p className="mt-2 text-sm text-gray-600">Leave blank if you are unsure of the membership number. You can provide this later.</p>
          <label className="mt-4 block text-sm font-medium text-gray-700">
            Membership number
            <input value={membershipNumber} onChange={event => setMembershipNumber(event.target.value)} disabled={Boolean(savingAction)} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2" />
          </label>
          <button type="button" onClick={() => saveClaimAction('naht', { naht_member: membershipNumber })} disabled={Boolean(savingAction)} className="mt-3 rounded-md bg-sas px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {savingAction === 'naht' ? 'Saving...' : 'Save membership number'}
          </button>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
        <div className="lg:col-span-3 space-y-5 min-w-0">
          <h2 className="text-lg font-semibold text-sas">Claim documents</h2>
          {evidenceLoading && <p role="status" className="flex items-center gap-2 text-sm text-sas"><FontAwesomeIcon icon={faSpinner} spin />Loading documents...</p>}
          {evidenceError && <p role="alert" className="rounded-md bg-red-50 p-4 text-sm text-red-700">{evidenceError}</p>}
          {!evidenceLoading && !evidenceError && evidenceSections.length === 0 && <p className="text-sm text-gray-500">No evidence has been requested for this claim.</p>}
      {evidenceSections.map((section) => (
        <section key={section.evidence_id} className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3 mb-4">
            <h3 className="text-base font-semibold text-sas">{section.evidence_title || 'Supporting evidence'}</h3>
            <div>
              <button
                onClick={() => toggleSectionVisibility(section.evidence_id)}
                className="inline-flex items-center border border-sas text-sas rounded-md overflow-hidden text-xs font-medium"
              >
                <span className="px-4 py-1">{section.visible ? 'Cancel upload' : 'Upload document'}</span>
                <span className="bg-sas text-white py-2 px-3 flex items-center justify-center">
                  <FontAwesomeIcon icon={faPlusSquare} />
                </span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {section.documents && section.documents.length > 0 ? (
              section.documents.map((doc, index) => {
                const extraFields = parseExtraContent(doc.extra_content)
                const id = doc.evidence_document_id
                const draft = documentDrafts[id]
                const saving = savingDocuments[id]
                const fields = evidenceFields(section, doc)
                const getExtra = (label) => getExtraContent(extraFields, label)

                return (
                  <div
                    key={index}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm p-3 text-sm"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-semibold text-gray-800">
                        {doc.resources?.resource_name || section.evidence_title}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded capitalize ${doc.evidence_document_status === "accepted"
                          ? "bg-green-100 text-green-700"
                          : doc.evidence_document_status === "pending"
                            ? "bg-orange-100 text-orange-700"
                            : doc.evidence_document_status === "rejected"
                              ? "bg-red-100 text-red-700"
                              : doc.evidence_document_status === "expired"
                                ? "bg-purple-100 text-purple-700"
                                : doc.evidence_document_status === "required"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-gray-100 text-gray-600"
                          }`}
                      >
                        {doc.evidence_document_status}
                      </span>
                    </div>

                    <p className="text-xs text-gray-500">
                      Submitted: {moment(doc.created_at).format("DD/MM/YYYY H:i:s")}
                    </p>

                    {doc.evidence_document_status === "required" ? (
                      <p className="text-xs font-medium text-red-600 mt-2">
                        Please upload a {section.evidence_title}
                      </p>
                    ) : (
                      <div className="mt-3 space-y-3">
                        {draft ? (
                          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                            {fields.map(field => {
                              const Input = field.type === 'textarea' ? 'textarea' : 'input'
                              return (
                                <label key={field.key} className="block text-xs font-medium text-gray-700">
                                  {field.label}
                                  <Input
                                    type={field.type === 'textarea' ? undefined : field.type}
                                    value={draft[field.key]}
                                    disabled={saving || (field.isDate && doc.evidence_document_status === 'accepted')}
                                    required={field.required}
                                    onChange={event => setDocumentDrafts(prev => ({ ...prev, [id]: { ...prev[id], [field.key]: event.target.value } }))}
                                    className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm disabled:opacity-50"
                                  />
                                </label>
                              )
                            })}
                            {documentErrors[id] && <p role="alert" className="text-xs text-red-700">{documentErrors[id]}</p>}
                            <div className="flex justify-end gap-2 border-t border-gray-200 pt-3">
                              <button type="button" disabled={saving} onClick={() => cancelDocumentEdit(id)} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 disabled:opacity-50">Cancel</button>
                              <button type="button" disabled={saving} onClick={() => saveDocument(doc, section)} className="rounded-md bg-sas px-3 py-2 text-xs font-medium text-white disabled:opacity-50">
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {fields.map(field => (
                              <p key={field.key} className="text-xs text-gray-700 whitespace-pre-wrap">
                                <strong>{field.label}:</strong>{' '}
                                {field.isDate ? (doc[field.key] ? moment(doc[field.key]).format('DD/MM/YYYY') : '—') : (getExtra(field.key) || '—')}
                              </p>
                            ))}
                            <div className="flex justify-end border-t border-gray-100 pt-3">
                              <button type="button" onClick={() => {
                                setDocumentErrors(prev => ({ ...prev, [id]: '' }))
                                setDocumentDrafts(prev => ({ ...prev, [id]: Object.fromEntries(fields.map(field => [field.key,
                                  field.isDate ? (doc[field.key] ? moment(doc[field.key]).format('YYYY-MM-DD') : '') : getExtra(field.key),
                                ])) }))
                              }} className="inline-flex items-center gap-2 rounded-md border border-sas px-3 py-2 text-xs font-medium text-sas">
                                <FontAwesomeIcon icon={faPencil} /> Edit
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="col-span-full rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-500">No {section.evidence_title || 'supporting evidence'} documents uploaded yet.</p>
            )}
          </div>

          {section.visible && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {evidenceFields(section).map(field => {
                  const Input = field.type === 'textarea' ? 'textarea' : 'input'
                  return (
                    <label key={field.key} className="block text-sm font-medium text-gray-700">
                      {field.label}
                      <Input
                        type={field.type === 'textarea' ? undefined : field.type}
                        required={field.required}
                        disabled={uploadingSections[section.evidence_id]}
                        value={section.uploadValues?.[field.key] || ''}
                        onChange={event => updateSectionDate(section.evidence_id, 'uploadValues', { ...section.uploadValues, [field.key]: event.target.value })}
                        className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      />
                    </label>
                  )
                })}
              </div>
              {uploadErrors[section.evidence_id] && <p role="alert" className="text-sm text-red-700">{uploadErrors[section.evidence_id]}</p>}
                    <div className='border border-dashed border-gray-300 rounded-md'>
                      <label
                        htmlFor={`file-upload-${section.evidence_id}`}
                        ref={wrapperRef}
                        onDragEnter={onDragEnter}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        className=" p-6 text-center relative"
                      >
                        <input
                          type="file"
                          aria-label="Evidence file"
                          id={`file-upload-${section.evidence_id}`}
                          accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          onChange={onFileUpload}
                        />
                        <div className="mb-3 pointer-events-none">
                          <img src="/images/laptop.png" alt="Upload" className="mx-auto h-20" />
                        </div>
                        <p className="text-sas font-semibold text-sm pointer-events-none">Drag and Drop or click to upload a file</p>
                        <p className="text-xs text-gray-500 pointer-events-none">PNG, JPG, XLS or PDF up to 10MB</p>
                      </label>
                    </div>

                    <div className="grid grid-cols-3 gap-3 mt-8">
                      {fileList.map((item, index) => (
                        <div key={index} className="drop-file-preview__item flex items-center justify-between">
                          <div className="flex items-center">
                            <img
                              src={ImageConfig[item.type.split('/')[1]] || ImageConfig['default']}
                              alt=""
                              className="w-16"
                            />
                            <div className="drop-file-preview__item__info">
                              <p className="break-words">{item.name}</p>
                              <p>{item.size} KB</p>
                            </div>
                          </div>
                          <span
                            className="hover:text-red-700 drop-file-preview__item__del"
                            onClick={() => fileRemove(item)}
                          >
                            <FontAwesomeIcon icon={faCircleXmark} className="text-gray-700 hover:text-red-700 w-5 h-5" />
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleUpload(section)}
                        disabled={uploadingSections[section.evidence_id]}
                        className={`inline-flex items-center border border-sas rounded-md overflow-hidden text-sm font-medium ${uploadingSections[section.evidence_id] ? 'opacity-50 cursor-not-allowed' : 'text-sas'
                          }`}
                      >
                        <span className="px-4 py-1"> {uploadingSections[section.evidence_id] ? 'Uploading...' : 'Upload'}</span>
                        <span className="bg-sas text-white py-2 px-3 flex items-center justify-center">
                          {uploadingSections[section.evidence_id] ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faUpload} />}
                        </span>
                      </button>

                      {section.evidence_template_alt && (
                        <a
                          href={section.evidence_template_alt}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center border border-sas text-sas rounded-md overflow-hidden text-sm font-medium hover:shadow-md"
                        >
                          <span className="px-4 py-1">Download</span>
                          <span className="bg-sas text-white py-2 px-3 flex items-center justify-center">
                            <FontAwesomeIcon icon={faDownload} />
                          </span>
                        </a>
                      )}
                    </div>
            </div>
          )}
        </section>
      ))}
        </div>
        <aside className="space-y-4 lg:sticky lg:top-4">
      {expiryDate && (
        <div className="bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded-md text-sm text-center">
          <p className="text-base font-semibold">
            This claim will be closed {moment(expiryDate).diff(moment().startOf('day'), 'days') === 0 ? 'today' : moment(expiryDate).diff(moment().startOf('day'), 'days') === 1 ? 'tomorrow' : `in ${moment(expiryDate).diff(moment().startOf('day'), 'days')} days`}
          </p>
          <p>
            If requested documents are not received the claim will be closed on{' '}
            {moment(expiryDate).format('Do MMMM YYYY')}
          </p>
        </div>
      )}

          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600">Document status</h2>
            <ul className="mt-4 space-y-3">
              {[
                ['Accepted', 'bg-green-100 text-green-700'],
                ['Pending', 'bg-orange-100 text-orange-700'],
                ['Rejected', 'bg-red-100 text-red-700'],
                ['Expired', 'bg-purple-100 text-purple-700'],
                ['Required', 'bg-blue-100 text-blue-700'],
              ].map(([label, colour]) => (
                <li key={label}><span className={`inline-flex rounded px-2 py-1 text-xs font-medium ${colour}`}>{label}</span></li>
              ))}
            </ul>
          </div>
        </aside>
      </div>

      <div className="mt-8">
        <button
          onClick={() => setShowPolicyDetails(prev => !prev)}
          className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 rounded-md text-left text-sm font-semibold"
        >
          Policy Details
          <span className="text-lg">{showPolicyDetails ? '▴' : '▾'}</span>
        </button>

        {showPolicyDetails && policy?.policy_information && (
          <div className="overflow-x-auto mt-4">
            <table className="min-w-full text-[10px] text-left text-gray-800 overflow-hidden rounded-lg">
              <thead >
                <tr className="bg-sas text-white text-xs text-start">
                  <th className=" px-4 py-2">Staff</th>
                  <th className=" px-4 py-2">FTE</th>
                  <th className=" px-4 py-2">Daily Reimbursement</th>
                  <th className=" px-4 py-2">Waiting period</th>
                  <th className=" px-4 py-2">Benefit Days</th>
                  <th className=" px-4 py-2">Stress Limit</th>
                  <th className=" px-4 py-2">Maternity Reimbursement</th>
                </tr>
              </thead>
              <tbody className="text-center text-gray-700 text-sm">
                {(() => {
                  const info = JSON.parse(policy.policy_information);
                  const items = info.items || [];

                  return items.map((item, idx) => (
                    <tr key={idx} className={idx === items.length - 1 ? "bg-white even:bg-gray-50 even:rounded-b-lg" : "bg-white even:bg-gray-50"}>
                      <td className="px-4 py-2 rounded-b-lg">{item.staffType}</td>
                      <td className="px-4 py-2">{parseFloat(item.fte).toFixed(2)}</td>
                      <td className="px-4 py-2">{item.benefit}</td>
                      <td className="px-4 py-2">{item.excess}</td>
                      <td className="px-4 py-2">{item.period}</td>
                      <td className="px-4 py-2">{item.stress}</td>
                      <td className="px-4 py-2">
                        {item.maternityCover ? item.maternityCover : 'Excluded'}
                      </td>
                    </tr>
                  ))
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default ViewClaim
